import { Spacing } from '@persona-id/design-tokens';
import { CaretDownIcon, ImageIcon, TransferInIcon } from '@persona-id/design-tokens/icons';
import { XIcon } from '@persona-id/design-tokens/icons/12px';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { COLORS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import FileDisplay, { FileInfo } from 'dashboard/components-lib/FileDisplay/FileDisplay';
import { ModalBody, ModalContainer } from 'dashboard/layouts/ModalLayout';
import { showMessage } from 'dashboard/redux/actions/messages';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import Sticker from 'design-system/components/Sticker';
import { Heading } from 'design-system/components/Text';
import IconMaximize from 'images/icons/maximize.svg';
import IconMinimize from 'images/icons/minimize.svg';
import download from 'lib/download';
import useCounter from 'lib/hooks/useCounter';
import { useLeftArrowListener, useRightArrowListener } from 'lib/hooks/useKeyListener';
import useToggle from 'lib/hooks/useToggle';

import 'react-pdf/dist/esm/Page/TextLayer.css';

const MIME_TYPE_TO_EXTENSION_MAP: Record<string, string> = {
  'application/pdf': 'PDF',
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
  'image/heic': 'HEIC',
  'image/heif': 'HEIF',
  'application/zip': 'ZIP',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.ms-excel': 'XLS',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'text/csv': 'CSV',
  'text/plain': 'TXT',
  'text/tab-separated-values': 'TSV',
};

export const mimeTypeToExtension = (mimeType?: string) => {
  if (!mimeType) {
    return 'UKNOWN';
  }

  return MIME_TYPE_TO_EXTENSION_MAP[mimeType] || mimeType.split('/').pop()?.toUpperCase();
};

const StyledModalContainer = styled(ModalContainer)`
  width: unset;
`;

const StyledModalBody = styled(ModalBody)`
  max-height: unset;
`;

interface ImageContainerProps {
  width?: string;
  height?: string;
  clickable?: boolean;
  circular?: boolean;
}

export const ImageContainer = styled('div')<ImageContainerProps>`
  position: relative;
  width: ${({ width }) => width || '150px'};
  min-height: ${({ height }) => height || '200px'};
  cursor: ${({ clickable }) => (clickable ? 'zoom-in' : 'inherit')};
  border-radius: ${({ circular }) => (circular ? '50%' : '8px')};
  overflow: hidden;
  border: ${({ circular }) => (circular ? '2px solid white' : null)};
`;

interface ImageProps {
  isLoaded: boolean;
}

const DefaultImage = styled('div')<ImageProps>`
  opacity: ${({ isLoaded }) => (isLoaded ? '0' : '1')};
  transition: opacity 0.3s;
  position: absolute;
  height: 100%;
  width: 100%;
`;

const EmptyImageMessage = styled('div')`
  background: ${({ theme }) => theme.color.background.secondary};
  padding: ${Spacing[2]};
  position: absolute;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const StyledIconImage = styled(ImageIcon)`
  margin-bottom: ${Spacing[2]};

  svg {
    color: ${COLORS.gray50};
  }
`;

const StickerContainer = styled('div')`
  position: absolute;
  top: ${Spacing[2]};
  left: ${Spacing[2]};
`;

const IconContainer = styled.div.attrs(() => ({ role: 'button' }))`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0 ${Spacing[2]};
  height: ${({ theme }) => theme.text.heading.md.lineHeight};

  & path {
    color: ${({ theme }) => theme.color.text.tertiary};

    &[stroke] {
      stroke: ${({ theme }) => theme.color.text.tertiary};
    }
  }

  &:hover {
    color: ${({ theme }) => theme.color.text.disabled};

    path {
      stroke: ${({ theme }) => theme.color.text.disabled};
    }
  }
`;

const Header = styled.header`
  flex: none;
  margin: 0;
  padding: 0 ${Spacing[7]};

  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const StyledIconMaximize = styled(IconMaximize)`
  height: ${Spacing[4]};
  width: ${Spacing[4]};
`;

const StyledIconMinimize = styled(IconMinimize)`
  height: ${Spacing[4]};
  width: ${Spacing[4]};
`;

interface AsyncImageProps {
  value?: FileInfo[];
  className?: string;
  /**
   * @deprecated
   * Only single images. Use `value` instead.
   */
  imageUrl?: string | null;
  placeholder?: JSX.Element;
  width?: string;
  height?: string;
  allowPreviewZoom?: boolean;
  allowModalExpand?: boolean;
  /**
   * When `string`, will be displayed for all images.
   * When `array`, `modalLabel[activeIndex]` will be displayed.
   */
  modalContents?: JSX.Element;
  modalLabel?: string;
  /**
   * Shows e.g. "1 of 5" in the label.
   */
  showCount?: boolean;
  showCountSticker?: boolean;
  showExtension?: boolean;
  isBeta?: boolean;
  referrerPolicy?: 'no-referrer' | 'origin' | 'unsafe-url';
  circular?: boolean;
}

const AsyncImage = ({
  imageUrl,
  className,
  value,
  placeholder = (
    <EmptyImageMessage>
      <StyledIconImage />
    </EmptyImageMessage>
  ),
  width,
  height,
  allowPreviewZoom = true,
  allowModalExpand,
  modalLabel,
  modalContents,
  referrerPolicy,
  showCount,
  showCountSticker,
  showExtension,
  isBeta,
  circular = false,
}: AsyncImageProps) => {
  // For legacy props.imageUrl
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  value = value ?? [{ url: imageUrl }];

  const [loadedImage, setLoadedImage] = useState(null);
  const [hasError, { enable: setError }] = useToggle();
  const [isModalOpen, { enable: showModal, disable: hideModal }] = useToggle();
  const [isModalExpanded, { enable: expandModal, disable: shrinkModal }] = useToggle();
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [activeIndex, { increment, decrement }] = useCounter(0, { mod: value.length });

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const loading = loadedImage !== value[0]?.url;
  const canOpenModal = !loading && !hasError && allowPreviewZoom;
  const canExpandModal = canOpenModal && allowModalExpand;

  const label = useMemo<string>(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const activeImageLabel = value[activeIndex]?.label ?? modalLabel;
    const countLabel =
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      showCount && value.length > 1 ? `${activeIndex + 1} of ${value.length}` : undefined;

    return [activeImageLabel, countLabel].filter(Boolean).join(' - ');
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  }, [modalLabel, activeIndex, showCount, value.length]);

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  if (value.length === 0) {
    return (
      <div className={className}>
        <ImageContainer
          width={width}
          height={height}
          clickable={false}
          circular={circular}
          id='async-image__container'
          data-test='async-image__container'
        >
          <EmptyImageMessage data-test='async-image__empty'>
            <StyledIconImage /> No image
          </EmptyImageMessage>
        </ImageContainer>
      </div>
    );
  }

  return (
    <div className={className}>
      <ImageContainer
        width={width}
        height={height}
        clickable={canOpenModal}
        circular={circular}
        id='async-image__container'
        data-test='async-image__container'
        onClick={() => canOpenModal && showModal()}
      >
        <DefaultImage isLoaded={!loading}>{placeholder}</DefaultImage>

        {hasError ? (
          <EmptyImageMessage data-test='async-image__error'>
            <StyledIconImage /> Failed to load image
          </EmptyImageMessage>
        ) : (
          <FileDisplay
            data-test='async-image__thumbnail'
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            file={value[0]}
            loading={loading}
            onFileLoad={() => {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              setLoadedImage(value[0]?.url);
            }}
            onError={setError}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            height={parseFloat(height)}
            position='absolute'
            referrerPolicy={referrerPolicy}
            thumbnail
          />
        )}

        {showCountSticker && (
          <StickerContainer>
            <Sticker variant='primary'>
              {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
              {`${value.length} File${value.length !== 1 ? 's' : ''}`}
            </Sticker>
          </StickerContainer>
        )}

        {showExtension && value?.[0]?.type != null && (
          <StickerContainer>
            <Sticker variant='primary'>{mimeTypeToExtension(value?.[0]?.type)}</Sticker>
            {isBeta && (
              <div>
                <Sticker variant='caution'>BETA</Sticker>
              </div>
            )}
          </StickerContainer>
        )}
      </ImageContainer>

      {!hasError && (
        <ModalWrapper isOpen={isModalOpen} handleCloseModal={hideModal}>
          <StyledModalContainer data-test='async-image__modal-container'>
            <Header data-test='async-image__modal-header'>
              <Heading size='md'>{label}</Heading>
              <div style={{ display: 'flex' }}>
                {canExpandModal && !isModalExpanded && (
                  <IconContainer id='modal__expand' onClick={expandModal} data-test='modal__expand'>
                    <StyledIconMaximize />
                  </IconContainer>
                )}
                {canExpandModal && isModalExpanded && (
                  <IconContainer id='modal__shrink' onClick={shrinkModal} data-test='modal__shrink'>
                    <StyledIconMinimize />
                  </IconContainer>
                )}
                <IconContainer id='modal__close' onClick={hideModal} data-test='modal__close'>
                  <XIcon />
                </IconContainer>
              </div>
            </Header>
            <StyledModalBody>
              {modalContents || (
                <AsyncImageModal
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  file={value[activeIndex]}
                  placeholder={placeholder}
                  increment={increment}
                  decrement={decrement}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  multi={value.length > 1}
                  expanded={isModalExpanded}
                />
              )}
            </StyledModalBody>
          </StyledModalContainer>
        </ModalWrapper>
      )}
    </div>
  );
};

export default AsyncImage;

interface AsyncImageModalBodyProps {
  file: FileInfo;
  placeholder: JSX.Element;
  increment: () => void;
  decrement: () => void;
  multi: boolean;
  expanded: boolean;
}
const AsyncImageModal = ({
  file,
  placeholder,
  increment,
  decrement,
  multi,
  expanded,
}: AsyncImageModalBodyProps) => {
  const [loading, { enable: startLoading, disable: stopLoading }] = useToggle(true);
  const [hasError, { enable: setError, disable: clearError }] = useToggle();

  const dispatch = useDispatch();

  const handlePrev = useCallback(() => {
    if (!multi) {
      return;
    }
    clearError();
    startLoading();
    decrement();
  }, [multi, decrement]);

  const handleNext = useCallback(() => {
    if (!multi) {
      return;
    }
    clearError();
    startLoading();
    increment();
  }, [multi, increment]);
  useLeftArrowListener(handlePrev);
  useRightArrowListener(handleNext);

  const downloadFile = async () => {
    try {
      await download({
        url: file.url,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        filename: file.name,
      });
    } catch {
      dispatch(showMessage('Failed to download file. Please try again.'));
    }
  };

  const canDownload = !hasError && file.name != null;

  return (
    <ImageContainer
      width={expanded ? '80vw' : '650px'}
      height={expanded ? '70vh' : '480px'}
      data-test='async-image-modal'
    >
      {multi && (
        <>
          <NavButtonContainer dir='left'>
            <Button
              id='async-image-modal__btn-decrement'
              data-test='async-image-modal__btn-decrement'
              onClick={handlePrev}
              width='icon'
              variant='inverse'
            >
              <CaretDownIcon />
            </Button>
          </NavButtonContainer>
          <NavButtonContainer dir='right'>
            <Button
              id='async-image-modal__btn-increment'
              data-test='async-image-modal__btn-increment'
              onClick={handleNext}
              width='icon'
              variant='inverse'
            >
              <CaretDownIcon />
            </Button>
          </NavButtonContainer>
        </>
      )}
      {canDownload && (
        <DownloadContainer>
          <Button
            id='async-image-modal__btn-download'
            data-test='async-image-modal__btn-download'
            onClick={downloadFile}
            width='icon'
            variant='primary'
          >
            <TransferInIcon />
          </Button>
        </DownloadContainer>
      )}
      <DefaultImage isLoaded={!loading}>{placeholder}</DefaultImage>
      {hasError ? (
        <EmptyImageMessage>
          <StyledIconImage data-test='async-image-modal__error' /> Failed to load image
        </EmptyImageMessage>
      ) : (
        <FileDisplay
          data-test='async-image-modal__image'
          file={file}
          loading={loading}
          onFileLoad={stopLoading}
          onError={setError}
          position='absolute'
        />
      )}
    </ImageContainer>
  );
};

const NavButtonContainer = styled.div<{ dir: 'left' | 'right' }>`
  position: absolute;
  height: 100%;
  display: flex;
  align-items: center;
  z-index: ${Z_INDICES.modalElevated};

  ${({ dir }) =>
    dir === 'left'
      ? css`
          left: ${Spacing[4]};
          svg {
            transform: rotate(90deg);
          }
        `
      : css`
          right: ${Spacing[4]};
          svg {
            transform: rotate(270deg);
          }
        `}
`;

const DownloadContainer = styled.div`
  position: absolute;
  z-index: ${Z_INDICES.modalElevated};
  top: ${Spacing[4]};
  right: ${Spacing[4]};
`;
